import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Stack,
  Badge,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
  GridToolbar,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Inventory as StockIcon,
  Build as TechnicianIcon,
  Person as ClientIcon,
  Devices as DevicesIcon,
  Assignment as AssignIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  SwapHoriz as SwapIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Settings as GeneralIcon,
  Build as RevisionIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { apiService } from '../../../services/api';
import { Device, Client, User } from '../../../types';

interface DeviceGroupsProps {}

const DeviceGroups: React.FC<DeviceGroupsProps> = () => {
  const navigate = useNavigate();
  const { groupType } = useParams<{ groupType?: 'stock' | 'technician' | 'general' | 'client' }>();
  const [currentTab, setCurrentTab] = useState(groupType || 'stock');
  const [searchQuery, setSearchQuery] = useState('');
  const [assignDialog, setAssignDialog] = useState<{
    open: boolean;
    device?: Device;
    type?: 'technician' | 'client';
  }>({ open: false });
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');

  // API calls for devices by group
  const {
    data: stockDevices,
    loading: stockLoading,
    execute: fetchStockDevices,
  } = useApi(() => apiService.getNotInstalledDevices());

  const {
    data: technicianDevices,
    loading: technicianLoading,
    execute: fetchTechnicianDevices,
  } = useApi(() => apiService.getDevicesByGroup('technician'));

  const {
    data: clientDevices,
    loading: clientLoading,
    execute: fetchClientDevices,
  } = useApi(() => apiService.getDevicesByGroup('client'));

  // API call for general devices (installed devices)
  const {
    data: generalDevices,
    loading: generalLoading,
    execute: fetchGeneralDevices,
  } = useApi(() => apiService.getDevicesByGroup('general'));

  // API calls for users and clients
  const {
    data: technicians,
    execute: fetchTechnicians,
  } = useApi(() => apiService.getTechnicians());

  const {
    data: clients,
    execute: fetchClients,
  } = useApi(() => apiService.getClients(1, 1000, ''));

  const {
    data: vehiclesData,
    execute: fetchVehicles,
  } = useApi(() => selectedClient ? apiService.getVehiclesByClient(selectedClient) : Promise.resolve({ data: [] }));

  useEffect(() => {
    fetchStockDevices();
    fetchTechnicianDevices();
    fetchGeneralDevices();
    fetchClientDevices();
    fetchTechnicians();
    fetchClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchVehicles();
    }
  }, [selectedClient]);

  useEffect(() => {
    if (groupType && groupType !== currentTab) {
      setCurrentTab(groupType);
    }
  }, [groupType]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue as 'stock' | 'technician' | 'general' | 'client');
    navigate(`/devices/groups/${newValue}`);
  };

  const handleAssignToTechnician = (device: Device) => {
    setAssignDialog({
      open: true,
      device,
      type: 'technician',
    });
  };

  const handleAssignToClient = (device: Device) => {
    setAssignDialog({
      open: true,
      device,
      type: 'client',
    });
  };

  const handleReturnToStock = async (deviceId: string) => {
    if (window.confirm('¿Estás seguro de que quieres devolver este dispositivo al stock?')) {
      try {
        await apiService.returnDeviceToStock(deviceId, 'Devuelto desde interfaz de grupos');
        refreshAllData();
      } catch (error) {
        console.error('Error returning device to stock:', error);
      }
    }
  };

  const handlePutInRevision = (device: Device) => {
    navigate(`/devices/${device._id}/revision`);
  };

  const handleConfirmAssignment = async () => {
    if (!assignDialog.device) return;

    try {
      if (assignDialog.type === 'technician' && selectedTechnician) {
        await apiService.assignDeviceToTechnician(assignDialog.device._id, selectedTechnician);
      } else if (assignDialog.type === 'client' && selectedClient) {
        await apiService.assignDeviceToClient(
          assignDialog.device._id,
          selectedClient,
          selectedVehicle || undefined
        );
      }

      setAssignDialog({ open: false });
      setSelectedTechnician('');
      setSelectedClient('');
      setSelectedVehicle('');
      refreshAllData();
    } catch (error) {
      console.error('Error assigning device:', error);
    }
  };

  const refreshAllData = () => {
    fetchStockDevices();
    fetchTechnicianDevices();
    fetchGeneralDevices();
    fetchClientDevices();
  };

  const getDeviceStatusIcon = (device: Device) => {
    if (device.traccarStatus === 'online') {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />;
    } else {
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />;
    }
  };

  const getCommonColumns = (actions: any) => [
    {
      field: 'imei',
      headerName: 'IMEI',
      width: 180,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            <DevicesIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {params.value}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getDeviceStatusIcon(params.row)}
              <Typography variant="caption" color="text.secondary">
                {params.row.traccarStatus || 'offline'}
              </Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Nombre',
      width: 150,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {params.value || 'Sin nombre'}
        </Typography>
      ),
    },
    {
      field: 'brand_model',
      headerName: 'Marca/Modelo',
      width: 150,
      renderCell: (params: any) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.row.brand}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.model}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'lastUpdate',
      headerName: 'Última Actualización',
      width: 180,
      renderCell: (params: any) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? new Date(params.value).toLocaleString('es-DO') : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 150,
      getActions: actions,
    },
  ] as GridColDef[];

  const stockColumns = getCommonColumns((params: GridRowParams<Device>) => [
    <GridActionsCellItem
      icon={<TechnicianIcon />}
      label="Asignar a Técnico"
      onClick={() => handleAssignToTechnician(params.row)}
    />,
  ]);

  const technicianColumns = [
    ...getCommonColumns((params: GridRowParams<Device>) => [
      <GridActionsCellItem
        icon={<ClientIcon />}
        label="Asignar a Cliente"
        onClick={() => handleAssignToClient(params.row)}
      />,
      <GridActionsCellItem
        icon={<StockIcon />}
        label="Devolver a Stock"
        onClick={() => handleReturnToStock(params.row._id)}
      />,
    ]),
    {
      field: 'technicianId',
      headerName: 'Técnico',
      width: 150,
      renderCell: (params: any) => {
        const technician = params.value as User;
        return technician ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'info.main' }}>
              <TechnicianIcon fontSize="small" />
            </Avatar>
            <Typography variant="body2">
              {technician.name}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin asignar
          </Typography>
        );
      },
    },
  ] as GridColDef[];

  const clientColumns = [
    ...getCommonColumns((params: GridRowParams<Device>) => [
      <GridActionsCellItem
        icon={<StockIcon />}
        label="Devolver a Stock"
        onClick={() => handleReturnToStock(params.row._id)}
      />,
    ]),
    {
      field: 'clientId',
      headerName: 'Cliente',
      width: 150,
      renderCell: (params: any) => {
        const client = params.value as Client;
        return client ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main' }}>
              <ClientIcon fontSize="small" />
            </Avatar>
            <Typography variant="body2">
              {client.name}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin asignar
          </Typography>
        );
      },
    },
    {
      field: 'vehicleId',
      headerName: 'Vehículo',
      width: 120,
      renderCell: (params: any) => {
        const vehicle = params.value;
        return vehicle ? (
          <Chip
            label={vehicle.plate || 'Vehículo'}
            size="small"
            color="primary"
            variant="outlined"
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin vehículo
          </Typography>
        );
      },
    },
  ] as GridColDef[];

  const generalColumns = [
    ...getCommonColumns((params: GridRowParams<Device>) => [
      <GridActionsCellItem
        icon={<RevisionIcon />}
        label="Poner en Revisión"
        onClick={() => handlePutInRevision(params.row)}
      />,
      <GridActionsCellItem
        icon={<StockIcon />}
        label="Devolver a Stock"
        onClick={() => handleReturnToStock(params.row._id)}
      />,
    ]),
    {
      field: 'clientId',
      headerName: 'Cliente',
      width: 150,
      renderCell: (params: any) => {
        const client = params.value as Client;
        return client ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main' }}>
              <ClientIcon fontSize="small" />
            </Avatar>
            <Typography variant="body2">
              {client.name}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin asignar
          </Typography>
        );
      },
    },
    {
      field: 'vehicleId',
      headerName: 'Vehículo',
      width: 120,
      renderCell: (params: any) => {
        const vehicle = params.value;
        return vehicle ? (
          <Chip
            label={vehicle.plate || 'Vehículo'}
            size="small"
            color="primary"
            variant="outlined"
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin vehículo
          </Typography>
        );
      },
    },
  ] as GridColDef[];

  const getCurrentDevices = () => {
    switch (currentTab) {
      case 'stock':
        return (stockDevices as any)?.data || [];
      case 'technician':
        return (technicianDevices as any)?.data || [];
      case 'general':
        return (generalDevices as any)?.data || [];
      case 'client':
        return (clientDevices as any)?.data || [];
      default:
        return [];
    }
  };

  const getCurrentLoading = () => {
    switch (currentTab) {
      case 'stock':
        return stockLoading;
      case 'technician':
        return technicianLoading;
      case 'general':
        return generalLoading;
      case 'client':
        return clientLoading;
      default:
        return false;
    }
  };

  const getCurrentColumns = () => {
    switch (currentTab) {
      case 'stock':
        return stockColumns;
      case 'technician':
        return technicianColumns;
      case 'general':
        return generalColumns;
      case 'client':
        return clientColumns;
      default:
        return stockColumns;
    }
  };

  const getTabLabel = (tab: string, count: number) => {
    const labels = {
      stock: 'Stock',
      technician: 'Técnicos',
      general: 'General',
      client: 'Clientes',
    };
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography>{labels[tab as keyof typeof labels]}</Typography>
        <Badge badgeContent={count} color="primary" />
      </Box>
    );
  };

  const filteredDevices = getCurrentDevices().filter((device: Device) =>
    searchQuery === '' ||
    device.imei.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.model.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Gestión de Grupos de Dispositivos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Flujo: Stock → Técnico → General → Cliente
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshAllData}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<DevicesIcon />}
            onClick={() => navigate('/devices/new')}
          >
            Nuevo Dispositivo
          </Button>
        </Box>
      </Box>

      {/* Workflow Overview */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Flujo de Trabajo
          </Typography>
          <Grid container spacing={1} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
              <Card sx={{ backgroundColor: 'warning.50', textAlign: 'center', p: 2 }}>
                <StockIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {(stockDevices as any)?.data?.length || 0}
                </Typography>
                <Typography variant="body2">En Stock</Typography>
                <Typography variant="caption" color="text.secondary">
                  Disponibles
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 0.5 }} sx={{ textAlign: 'center' }}>
              <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
              <Card sx={{ backgroundColor: 'info.50', textAlign: 'center', p: 2 }}>
                <TechnicianIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {(technicianDevices as any)?.data?.length || 0}
                </Typography>
                <Typography variant="body2">Técnicos</Typography>
                <Typography variant="caption" color="text.secondary">
                  Para instalar
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 0.5 }} sx={{ textAlign: 'center' }}>
              <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
              <Card sx={{ backgroundColor: 'purple.50', textAlign: 'center', p: 2 }}>
                <GeneralIcon sx={{ fontSize: 32, color: 'purple.main', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {(generalDevices as any)?.data?.length || 0}
                </Typography>
                <Typography variant="body2">General</Typography>
                <Typography variant="caption" color="text.secondary">
                  Instalados
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 0.5 }} sx={{ textAlign: 'center' }}>
              <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
              <Card sx={{ backgroundColor: 'success.50', textAlign: 'center', p: 2 }}>
                <ClientIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {(clientDevices as any)?.data?.length || 0}
                </Typography>
                <Typography variant="body2">Clientes</Typography>
                <Typography variant="caption" color="text.secondary">
                  Activos
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs and Content */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="device groups tabs"
          >
            <Tab
              label={getTabLabel('stock', (stockDevices as any)?.data?.length || 0)}
              value="stock"
              icon={<StockIcon />}
              iconPosition="start"
            />
            <Tab
              label={getTabLabel('technician', (technicianDevices as any)?.data?.length || 0)}
              value="technician"
              icon={<TechnicianIcon />}
              iconPosition="start"
            />
            <Tab
              label={getTabLabel('general', (generalDevices as any)?.data?.length || 0)}
              value="general"
              icon={<GeneralIcon />}
              iconPosition="start"
            />
            <Tab
              label={getTabLabel('client', (clientDevices as any)?.data?.length || 0)}
              value="client"
              icon={<ClientIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <CardContent>
          {/* Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por IMEI, nombre, marca o modelo..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>

          {/* Device List */}
          <DataGrid
            rows={filteredDevices}
            columns={getCurrentColumns()}
            loading={getCurrentLoading()}
            pagination
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            getRowId={(row) => row._id}
            slots={{
              toolbar: GridToolbar,
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              border: 0,
              minHeight: 400,
            }}
          />
        </CardContent>
      </Card>

      {/* Assignment Dialog */}
      <Dialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {assignDialog.type === 'technician' ? 'Asignar a Técnico' : 'Asignar a Cliente'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {assignDialog.device && (
              <Alert severity="info">
                Asignando dispositivo: <strong>{assignDialog.device.name || assignDialog.device.imei}</strong>
              </Alert>
            )}

            {assignDialog.type === 'technician' ? (
              <FormControl fullWidth>
                <InputLabel>Técnico</InputLabel>
                <Select
                  value={selectedTechnician}
                  label="Técnico"
                  onChange={(e) => setSelectedTechnician(e.target.value)}
                >
                  {(technicians as any)?.data?.map((tech: User) => (
                    <MenuItem key={tech._id} value={tech._id}>
                      {tech.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>Cliente</InputLabel>
                  <Select
                    value={selectedClient}
                    label="Cliente"
                    onChange={(e) => setSelectedClient(e.target.value)}
                  >
                    {(clients as any)?.data?.map((client: Client) => (
                      <MenuItem key={client._id} value={client._id}>
                        {client.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedClient && (
                  <FormControl fullWidth>
                    <InputLabel>Vehículo (Opcional)</InputLabel>
                    <Select
                      value={selectedVehicle}
                      label="Vehículo (Opcional)"
                      onChange={(e) => setSelectedVehicle(e.target.value)}
                    >
                      <MenuItem value="">Sin vehículo específico</MenuItem>
                      {(vehiclesData as any)?.data?.map((vehicle: any) => (
                        <MenuItem key={vehicle._id} value={vehicle._id}>
                          {vehicle.plate} - {vehicle.make} {vehicle.model}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAssignment}
            disabled={
              assignDialog.type === 'technician'
                ? !selectedTechnician
                : !selectedClient
            }
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceGroups;